import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Users, CheckSquare, Calendar, MessageSquare, Loader2 } from 'lucide-react';

const CircleInspectorDialog = ({ open, onOpenChange, circleId, circleName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('members');

  useEffect(() => {
    if (!open || !circleId) return;
    setLoading(true);
    axios.get(`${API}/admin/circles/${circleId}/details`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, circleId]);

  const tasks = data ? [...(data.tasks_v2 || []), ...(data.tasks_legacy || [])] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="circle-inspector">
        <DialogHeader>
          <DialogTitle>Inspect: {circleName}</DialogTitle>
          <p className="text-xs text-muted-foreground">Read-only. You are not a member of this circle and nobody was notified.</p>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : !data ? (
          <p className="py-8 text-center text-muted-foreground">Could not load circle details.</p>
        ) : (
          <>
            {/* Stat pills */}
            <div className="flex gap-2 flex-wrap py-2">
              {[
                { key: 'members', icon: Users, label: `${data.counts.members} Members`, color: 'text-cyan-600 bg-cyan-50' },
                { key: 'tasks', icon: CheckSquare, label: `${data.counts.tasks_v2 + data.counts.tasks_legacy} Tasks`, color: 'text-violet-600 bg-violet-50' },
                { key: 'events', icon: Calendar, label: `${data.counts.events} Events`, color: 'text-emerald-600 bg-emerald-50' },
                { key: 'updates', icon: MessageSquare, label: `${data.counts.updates} Updates`, color: 'text-amber-600 bg-amber-50' },
              ].map(t => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${active ? `${t.color} ring-2 ring-offset-1 ring-current/30` : 'bg-muted text-muted-foreground hover:bg-muted-foreground/10'}`}
                    data-testid={`inspector-tab-${t.key}`}>
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="overflow-y-auto flex-1 py-3">
              {tab === 'members' && (
                <div className="space-y-2">
                  {data.members.map(m => (
                    <div key={m.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{m.name} {m.is_pending && <span className="text-xs text-amber-600 ml-1">(pending)</span>}</p>
                        <p className="text-xs text-muted-foreground">{m.email}</p>
                      </div>
                      <span className="text-xs bg-muted px-2 py-0.5 rounded-full">{m.circle_role || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'tasks' && (
                <div className="space-y-2">
                  {tasks.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No tasks yet.</p>}
                  {tasks.map(t => (
                    <div key={t.id} className="border border-border rounded-lg px-3 py-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-semibold">{t.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>
                        </div>
                        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${
                          t.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          t.status === 'in_progress' ? 'bg-violet-50 text-violet-700' :
                          'bg-cyan-50 text-cyan-700'}`}>{t.status || 'open'}</span>
                      </div>
                      {(t.claimed_by_name || t.assigned_to) && (
                        <p className="text-[10px] text-muted-foreground mt-1">Claimed by {t.claimed_by_name || t.assigned_to}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {tab === 'events' && (
                <div className="space-y-2">
                  {data.calendar_events.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No events scheduled.</p>}
                  {data.calendar_events.map(e => (
                    <div key={e.id} className="border border-border rounded-lg px-3 py-2">
                      <p className="text-sm font-semibold">{e.title}</p>
                      <p className="text-xs text-muted-foreground">{new Date(e.start_time).toLocaleString()} · {e.location || '—'}</p>
                      {e.description && <p className="text-xs text-muted-foreground mt-1">{e.description}</p>}
                    </div>
                  ))}
                </div>
              )}
              {tab === 'updates' && (
                <div className="space-y-2">
                  {data.updates.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No updates posted yet.</p>}
                  {data.updates.map(u => (
                    <div key={u.id} className="border border-border rounded-lg px-3 py-2">
                      <p className="text-xs text-muted-foreground">{u.user_name} · {new Date(u.created_at).toLocaleString()}</p>
                      <p className="text-sm mt-1">{u.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CircleInspectorDialog;
